import { AudioUpload } from "@/components/AudioUpload";
import { ImageUpload } from "@/components/ImageUpload";
import { GenerateVocabImageDocument, SynthesizeSpeechDocument } from "@/generated/graphql";
import { useAddVocabCard } from "@/lib/hooks/vocabCards/useAddVocabCard";
import { useUpdateVocabCard } from "@/lib/hooks/vocabCards/useUpdateVocabCard";
import { withToast } from "@/lib/utils/withToast";
import type { VocabCardFields } from "@/types/VocabCard";
import { Button, FormField, LoadingSpinner, Modal } from "@ai-teach/ui-web";
import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { MdAutoAwesome, MdEdit } from "react-icons/md";

type CreationMode = "generated" | "manual";

/** Decodes base64 media returned by AI generation into a File for the upload widgets. */
function base64ToFile(base64: string, filename: string, mimeType: string): File {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mimeType });
}

const PART_OF_SPEECH_OPTIONS = [
  { value: "", label: "Select part of speech" },
  { value: "noun", label: "Noun" },
  { value: "verb", label: "Verb" },
  { value: "adjective", label: "Adjective" },
  { value: "adverb", label: "Adverb" },
  { value: "pronoun", label: "Pronoun" },
  { value: "preposition", label: "Preposition" },
  { value: "conjunction", label: "Conjunction" },
  { value: "interjection", label: "Interjection" },
  { value: "article", label: "Article" },
];

interface AddVocabCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  vocabCard?: VocabCardFields | undefined; // Optional: if provided, modal is in edit mode
}

export function AddVocabCardModal({ isOpen, onClose, vocabCard }: AddVocabCardModalProps) {
  const isEditMode = !!vocabCard;

  const { addVocabCard, loading: addLoading } = useAddVocabCard();
  const { updateVocabCard, loading: updateLoading } = useUpdateVocabCard();

  const loading = addLoading || updateLoading;

  const [mode, setMode] = useState<CreationMode>("generated");
  const [formFields, setFormFields] = useState({
    word: vocabCard?.word || "",
    definition: vocabCard?.definition || "",
    example: vocabCard?.example || "",
    partOfSpeech: vocabCard?.partOfSpeech || "",
  });
  const [fieldErrors, setFieldErrors] = useState({
    word: false,
    definition: false,
    example: false,
    partOfSpeech: false,
    image: false,
    audio: false,
  });
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [selectedAudioFile, setSelectedAudioFile] = useState<File | null>(null);

  const [generateVocabImageMutation] = useMutation(GenerateVocabImageDocument);
  const [synthesizeSpeechMutation] = useMutation(SynthesizeSpeechDocument);

  // AI-generate the card image; returned File flows into ImageUpload's crop flow.
  const handleGenerateImage = async (): Promise<File | null> => {
    const word = formFields.word.trim();
    if (!word) {
      setFieldErrors((prev) => ({ ...prev, word: true }));
      return null;
    }
    const context = formFields.example.trim() || formFields.definition.trim() || null;
    try {
      return await withToast(
        async () => {
          const { data } = await generateVocabImageMutation({ variables: { word, context } });
          const base64 = data?.generateVocabImage?.imageBase64;
          return base64 ? base64ToFile(base64, `${word}.png`, "image/png") : null;
        },
        {
          loading: "Generating image...",
          success: "Image generated",
          error: "Failed to generate image",
        },
        "vocab-image-gen-toast"
      );
    } catch {
      return null;
    }
  };

  // TTS-generate the pronunciation audio for the word; returned File flows into AudioUpload.
  const handleGenerateAudio = async (): Promise<File | null> => {
    const word = formFields.word.trim();
    if (!word) {
      setFieldErrors((prev) => ({ ...prev, word: true }));
      return null;
    }
    try {
      return await withToast(
        async () => {
          const { data } = await synthesizeSpeechMutation({
            variables: { text: word, inline: true },
          });
          const base64 = data?.synthesizeSpeech?.audioBase64;
          return base64 ? base64ToFile(base64, `${word}.mp3`, "audio/mpeg") : null;
        },
        {
          loading: "Generating audio...",
          success: "Audio generated",
          error: "Failed to generate audio",
        },
        "vocab-audio-gen-toast"
      );
    } catch {
      return null;
    }
  };

  // Sync form state when vocabCard prop changes (edit mode)
  useEffect(() => {
    if (vocabCard) {
      setFormFields({
        word: vocabCard.word || "",
        definition: vocabCard.definition || "",
        example: vocabCard.example || "",
        partOfSpeech: vocabCard.partOfSpeech || "",
      });
      setMode("manual"); // Edit mode always uses manual mode
    }
  }, [vocabCard]);

  const handleSubmit = async () => {
    if (isEditMode && vocabCard) {
      // Edit mode: validate text fields (image/audio are optional - can keep existing)
      const errors = {
        word: !formFields.word.trim(),
        definition: !formFields.definition.trim(),
        example: !formFields.example.trim(),
        partOfSpeech: !formFields.partOfSpeech,
        image: false,
        audio: false,
      };

      setFieldErrors(errors);

      if (Object.values(errors).some((error) => error)) {
        return;
      }

      await updateVocabCard(vocabCard.id, {
        word: formFields.word.trim(),
        definition: formFields.definition.trim(),
        example: formFields.example.trim(),
        partOfSpeech: formFields.partOfSpeech,
        imageFile: selectedImageFile,
        audioFile: selectedAudioFile,
      });
    } else if (mode === "generated") {
      // AI mode: only word is required
      if (!formFields.word.trim()) {
        setFieldErrors((prev) => ({ ...prev, word: true }));
        return;
      }
      await addVocabCard(formFields.word.trim(), "generated");
    } else {
      // Manual mode: all fields required
      const errors = {
        word: !formFields.word.trim(),
        definition: !formFields.definition.trim(),
        example: !formFields.example.trim(),
        partOfSpeech: !formFields.partOfSpeech,
        image: !selectedImageFile,
        audio: !selectedAudioFile,
      };

      setFieldErrors(errors);

      if (Object.values(errors).some((error) => error)) {
        return;
      }

      await addVocabCard(formFields.word.trim(), "manual", {
        word: formFields.word.trim(),
        definition: formFields.definition.trim(),
        example: formFields.example.trim(),
        partOfSpeech: formFields.partOfSpeech,
        imageFile: selectedImageFile,
        audioFile: selectedAudioFile,
      });
    }
    handleClose();
  };

  const handleClose = () => {
    // Only reset if not in edit mode
    if (!isEditMode) {
      setMode("generated");
      setFormFields({
        word: "",
        definition: "",
        example: "",
        partOfSpeech: "",
      });
    }
    setFieldErrors({
      word: false,
      definition: false,
      example: false,
      partOfSpeech: false,
      image: false,
      audio: false,
    });
    setSelectedImageFile(null);
    setSelectedAudioFile(null);

    onClose();
  };

  const handleFieldChange = (field: keyof typeof formFields, value: string) => {
    setFormFields((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  // In edit mode or manual mode, show the full form
  const showFullForm = isEditMode || mode === "manual";

  return (
    <Modal
      isOpen={isOpen}
      onClose={loading ? () => {} : handleClose}
      title={isEditMode ? "Edit Vocabulary Card" : "Add Vocabulary Card"}
      size="lg"
      closeOnOverlayClick={false}
    >
      <div className="space-y-6 relative">
        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/80 backdrop-blur-sm rounded-lg">
            <div className="flex flex-col items-center gap-4">
              <LoadingSpinner size="l" className="text-secondary-700" />
              <p className="text-secondary-200">
                {isEditMode ? "Saving vocabulary card..." : "Generating vocabulary card..."}
              </p>
            </div>
          </div>
        )}
        {/* Mode Selector - Only show when adding, not editing */}
        {!isEditMode && (
          <div>
            <label className="block text-sm font-medium text-secondary-200 mb-3">
              Creation Method
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setMode("generated")}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  mode === "generated"
                    ? "border-primary-500 bg-primary-500/10 text-primary-500"
                    : "border-gray-800 bg-surface text-secondary-200 hover:border-gray-700"
                }`}
              >
                <MdAutoAwesome className="w-5 h-5" />
                <span className="font-medium">AI Generated</span>
              </button>
              <button
                type="button"
                onClick={() => setMode("manual")}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  mode === "manual"
                    ? "border-primary-500 bg-primary-500/10 text-primary-500"
                    : "border-gray-800 bg-surface text-secondary-200 hover:border-gray-700"
                }`}
              >
                <MdEdit className="w-5 h-5" />
                <span className="font-medium">Manual Entry</span>
              </button>
            </div>
          </div>
        )}

        {/* Description */}
        <p className="text-sm text-secondary-200">
          {isEditMode
            ? "Update the vocabulary card details. You can change the image and audio by uploading new files."
            : mode === "generated"
              ? "Enter a word to automatically generate a vocabulary card with definition, example sentence, and audio pronunciation using AI."
              : "Manually enter all vocabulary card details including image and audio."}
        </p>

        {!showFullForm ? (
          /* AI Mode - Only Word Input */
          <FormField
            id="word"
            name="word"
            label="Word"
            type="text"
            value={formFields.word}
            onChange={(e) => handleFieldChange("word", e.target.value)}
            required
            placeholder="Enter a word (e.g., together)"
            error={fieldErrors.word ? "Word is required" : undefined}
          />
        ) : (
          /* Manual/Edit Mode - All Fields */
          <>
            <div className="flex gap-6">
              {/* Left Column - Image Upload */}
              <div className="flex-1">
                <label
                  className={`block text-sm font-medium mb-2 ${fieldErrors.image ? "text-error-500" : "text-secondary-200"}`}
                >
                  {!isEditMode && <span className="text-error-500">*</span>} Image
                </label>
                <ImageUpload
                  onFileSelect={(file) => {
                    setSelectedImageFile(file);
                    if (fieldErrors.image) {
                      setFieldErrors((prev) => ({ ...prev, image: false }));
                    }
                  }}
                  {...(!isEditMode && {
                    onRemove: () => {
                      setSelectedImageFile(null);
                    },
                  })}
                  currentImageUrl={vocabCard?.imageUrl ?? null}
                  aspectRatio={4 / 3}
                  onAiGenerate={handleGenerateImage}
                />
                {fieldErrors.image && (
                  <p className="mt-1 text-sm text-error-500">Image is required</p>
                )}
              </div>

              {/* Right Column - Text Fields */}
              <div className="flex-1 space-y-4 flex flex-col justify-center">
                {/* Word */}
                <FormField
                  id="word"
                  name="word"
                  label="Word"
                  type="text"
                  value={formFields.word}
                  onChange={(e) => handleFieldChange("word", e.target.value)}
                  required
                  placeholder="Enter a word"
                  error={fieldErrors.word ? "Word is required" : undefined}
                />

                {/* Part of Speech */}
                <div>
                  <label
                    htmlFor="partOfSpeech"
                    className={`block text-sm font-medium mb-1 ${fieldErrors.partOfSpeech ? "text-error-500" : "text-secondary-200"}`}
                  >
                    <span className="text-error-500">*</span> Part of Speech
                  </label>
                  <select
                    id="partOfSpeech"
                    value={formFields.partOfSpeech}
                    onChange={(e) => handleFieldChange("partOfSpeech", e.target.value)}
                    className={`mt-1 block w-full px-3 py-2 bg-surface border rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      fieldErrors.partOfSpeech ? "border-error-500" : "border-gray-800"
                    }`}
                  >
                    {PART_OF_SPEECH_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.partOfSpeech && (
                    <p className="mt-1 text-sm text-error-500">Part of speech is required</p>
                  )}
                </div>

                {/* Definition */}
                <FormField
                  id="definition"
                  name="definition"
                  label="Definition"
                  type="text"
                  value={formFields.definition}
                  onChange={(e) => handleFieldChange("definition", e.target.value)}
                  required
                  placeholder="Enter a brief definition"
                  error={fieldErrors.definition ? "Definition is required" : undefined}
                />
              </div>
            </div>

            {/* Example Sentence - Full Width */}
            <FormField
              id="example"
              name="example"
              label="Example Sentence"
              type="text"
              value={formFields.example}
              onChange={(e) => handleFieldChange("example", e.target.value)}
              required
              placeholder="Enter an example sentence using the word"
              error={fieldErrors.example ? "Example sentence is required" : undefined}
            />

            {/* Audio Upload - Full Width */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${fieldErrors.audio ? "text-error-500" : "text-secondary-200"}`}
              >
                {!isEditMode && <span className="text-error-500">*</span>} Audio Pronunciation
              </label>
              <AudioUpload
                onFileSelect={(file) => {
                  setSelectedAudioFile(file);
                  if (fieldErrors.audio) {
                    setFieldErrors((prev) => ({ ...prev, audio: false }));
                  }
                }}
                {...(!isEditMode && {
                  onRemove: () => {
                    setSelectedAudioFile(null);
                  },
                })}
                currentAudioUrl={vocabCard?.audioUrl ?? null}
                onTtsGenerate={handleGenerateAudio}
              />
              {fieldErrors.audio && (
                <p className="mt-1 text-sm text-error-500">Audio is required</p>
              )}
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? (isEditMode ? "Saving..." : "Creating...") : isEditMode ? "Save" : "Create"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
